// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// GroupCallSession is a small-group MESH call: every participant holds a direct
// RTCPeerConnection to every other participant (good for ~3-5 people; an SFU is the
// scale follow-up). It reuses the same ephemeral m.call.signal transport as the 1:1
// CallSession, but each frame is ADDRESSED to a specific peer (target) and the call
// is keyed by the ROOM (call_id = roomID) so any member can join without knowing a
// random id. To avoid offer glare, for each pair the LOWER userID creates the offer.
//
// NOTE: not yet wired into the UI — see RTC.md "Group calls" for the integration plan
// (a GroupCallView grid + a per-room "active call" indicator). Kept self-contained so
// it can't affect the working 1:1 path.

import { enumerateInputs } from "./devices";
import { pcQuality, type CallQuality } from "./rtc";
import { decode, encode } from "./whiteboard/protocol";
import { OpDedup, WHITEBOARD_DC_ID, WHITEBOARD_DC_LABEL, stampAuthor } from "./whiteboard/transport";
import type { WireOp } from "./whiteboard/types";

type IceServer = { urls: string[]; username?: string; credential?: string };

export interface GroupSignal {
  call_id: string; // = roomID
  kind: "join" | "hello" | "offer" | "answer" | "candidates" | "bye";
  target?: string; // recipient userID; absent = broadcast to the room's call
  sdp?: string;
  candidates?: RTCIceCandidateInit[];
  video?: boolean;
}

export interface GroupCallEvents {
  onParticipants: (userIDs: string[]) => void; // peers we hold a connection to
  onPeerStream: (userID: string, stream: MediaStream | null) => void; // null on leave
  onLocalStream: (stream: MediaStream) => void;
  onEnded: () => void;
  onScreenShare?: (sharing: boolean) => void; // we started/stopped screen-sharing
  // onWhiteboardOp fires for a whiteboard op received from a peer. `author` is already
  // re-stamped to the sending peer (anti-spoof); de-duped by op id. The consumer
  // (Phase 2/3) routes it to the shared room-board engine.
  onWhiteboardOp?: (op: WireOp) => void;
}

// GroupCall is the common surface the UI drives, implemented by BOTH the peer-to-peer
// mesh (GroupCallSession) and the server-side SFU (SfuCallSession), so the Workspace
// swaps backends transparently based on the server's rtc.sfu flag.
export interface GroupCall {
  readonly isActive: boolean;
  readonly sendingVideo: boolean;
  join(roomID: string, video: boolean, reuseStream?: MediaStream): Promise<void>;
  hangup(): void;
  setMuted(muted: boolean): void;
  setCameraOff(off: boolean): void;
  upgradeToVideo(): Promise<void>;
  devices(): Promise<{ mics: MediaDeviceInfo[]; cameras: MediaDeviceInfo[] }>;
  setAudioInput(deviceId: string): Promise<void>;
  setVideoInput(deviceId: string): Promise<void>;
  flipCamera(): Promise<void>;
  // peerQuality grades the live link health to a specific remote participant.
  peerQuality(userID: string): Promise<CallQuality>;
  // sendOp fans a whiteboard op out to every participant over the active transport
  // (per-peer data channels in mesh; the m.whiteboard WS frame in SFU).
  sendOp(op: WireOp): void;
}

export class GroupCallSession implements GroupCall {
  private readonly peers = new Map<string, RTCPeerConnection>();
  private readonly wbChannels = new Map<string, RTCDataChannel>(); // per-peer whiteboard data channel
  private readonly wbDedup = new OpDedup(); // applying the same op id twice is a no-op
  private readonly pending = new Map<string, RTCIceCandidateInit[]>(); // candidates before remoteDescription
  private local: MediaStream | null = null;
  private roomID = "";
  private active = false;
  private video = false;

  constructor(
    private readonly sendSignal: (roomID: string, content: GroupSignal) => void,
    private readonly getIceServers: () => Promise<IceServer[]>,
    private readonly myUserID: string,
    private readonly events: GroupCallEvents,
  ) {}

  get isActive(): boolean {
    return this.active;
  }
  get roomId(): string {
    return this.roomID;
  }

  // join starts OR joins the group call in a room (call_id = roomID). Announcing our
  // presence makes every active participant connect to us (and vice versa).
  async join(roomID: string, video: boolean, reuseStream?: MediaStream): Promise<void> {
    if (this.active) return;
    if (!reuseStream && !navigator.mediaDevices?.getUserMedia) {
      throw new Error(globalThis.isSecureContext ? "no-mic" : "insecure-context");
    }
    this.roomID = roomID;
    this.video = video;
    this.active = true;
    this.local = reuseStream ?? (await navigator.mediaDevices.getUserMedia({ audio: true, video })); // green room hands us its preview stream
    this.events.onLocalStream(this.local);
    this.broadcast({ kind: "join", video });
  }

  get sendingVideo(): boolean {
    return !!this.local?.getVideoTracks().length;
  }

  setMuted(muted: boolean): void {
    this.local?.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }
  setCameraOff(off: boolean): void {
    this.local?.getVideoTracks().forEach((t) => (t.enabled = !off));
  }

  devices(): Promise<{ mics: MediaDeviceInfo[]; cameras: MediaDeviceInfo[] }> {
    return enumerateInputs();
  }
  setAudioInput(deviceId: string): Promise<void> {
    return this.switchTrack("audio", { deviceId: { exact: deviceId } });
  }
  setVideoInput(deviceId: string): Promise<void> {
    return this.switchTrack("video", { deviceId: { exact: deviceId } });
  }
  private facing: "user" | "environment" = "user";
  // flipCamera toggles front↔back by facingMode (the OS picks the right lens).
  async flipCamera(): Promise<void> {
    const current = (this.local?.getVideoTracks()[0]?.getSettings().facingMode as "user" | "environment" | undefined) ?? this.facing;
    const next: "user" | "environment" = current === "environment" ? "user" : "environment";
    await this.switchTrack("video", { facingMode: { exact: next } });
    this.facing = next;
  }
  // upgradeToVideo adds a camera to an in-progress audio call (default device).
  upgradeToVideo(): Promise<void> {
    return this.switchTrack("video", {});
  }

  // peerQuality grades the link to one participant from that peer's RTCPeerConnection.
  peerQuality(userID: string): Promise<CallQuality> {
    return pcQuality(this.peers.get(userID) ?? null);
  }

  // switchTrack swaps (or adds) a track on EVERY peer. replaceTrack needs no
  // renegotiation; adding a first video track does (per peer).
  private async switchTrack(kind: "audio" | "video", constraint: MediaTrackConstraints): Promise<void> {
    if (!this.local) return;
    const oldTracks = kind === "audio" ? this.local.getAudioTracks() : this.local.getVideoTracks();
    const wasEnabled = oldTracks[0]?.enabled ?? true;
    const stream = await navigator.mediaDevices.getUserMedia(kind === "audio" ? { audio: constraint } : { video: constraint });
    const track = stream.getTracks()[0];
    if (!track) return;
    track.enabled = wasEnabled;
    for (const [peerID, pc] of this.peers) {
      const sender = pc.getSenders().find((s) => s.track?.kind === kind);
      if (sender) {
        await sender.replaceTrack(track);
      } else {
        pc.addTrack(track, this.local);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        this.send(peerID, { kind: "offer", sdp: offer.sdp, video: true });
      }
    }
    for (const t of oldTracks) {
      this.local.removeTrack(t);
      t.stop();
    }
    this.local.addTrack(track);
    if (kind === "video") this.video = true;
    this.events.onLocalStream(this.local);
  }

  // onSignal routes an inbound group-call frame addressed to us (or broadcast).
  async onSignal(from: string, raw: unknown): Promise<void> {
    if (from === this.myUserID) return;
    const c = raw as GroupSignal;
    if (!c || typeof c.kind !== "string") return;
    if (c.target && c.target !== this.myUserID) return; // not for us
    if (!this.active || c.call_id !== this.roomID) return; // only our room's call
    switch (c.kind) {
      case "join":
        // A new peer joined: the lower userID offers; the higher invites the lower to.
        if (this.myUserID < from) await this.offerTo(from);
        else this.send(from, { kind: "hello" });
        return;
      case "hello":
        // The peer is asking us (the lower userID) to initiate.
        if (this.myUserID < from) await this.offerTo(from);
        return;
      case "offer": {
        const pc = await this.ensurePeer(from);
        await pc.setRemoteDescription({ type: "offer", sdp: c.sdp });
        await this.flush(from, pc);
        const ans = await pc.createAnswer();
        await pc.setLocalDescription(ans);
        this.send(from, { kind: "answer", sdp: ans.sdp });
        // A peer that joined AUDIO-ONLY offers without a video m-line, so our answer
        // can't carry our camera — if we're already sending video, renegotiate so the
        // newcomer receives it.
        if (this.sendingVideo && !(c.sdp ?? "").includes("m=video")) {
          const reoffer = await pc.createOffer();
          await pc.setLocalDescription(reoffer);
          this.send(from, { kind: "offer", sdp: reoffer.sdp, video: true });
        }
        return;
      }
      case "answer": {
        const pc = this.peers.get(from);
        if (!pc) return;
        await pc.setRemoteDescription({ type: "answer", sdp: c.sdp });
        await this.flush(from, pc);
        return;
      }
      case "candidates": {
        const pc = this.peers.get(from);
        for (const cand of c.candidates ?? []) {
          if (pc?.remoteDescription) await pc.addIceCandidate(cand).catch(() => undefined);
          else this.queue(from, cand);
        }
        return;
      }
      case "bye":
        this.removePeer(from);
        return;
    }
  }

  hangup(): void {
    if (this.active) this.broadcast({ kind: "bye" });
    for (const [peerID, pc] of this.peers) {
      this.closeWbChannel(peerID);
      // Null handlers before close() so the "closed" connectionstatechange can't re-enter removePeer().
      pc.ontrack = pc.onicecandidate = pc.onconnectionstatechange = null;
      pc.close();
    }
    this.peers.clear();
    this.wbChannels.clear();
    this.wbDedup.reset();
    this.pending.clear();
    this.local?.getTracks().forEach((t) => t.stop());
    this.local = null;
    this.active = false;
    this.roomID = "";
    this.events.onParticipants([]);
    this.events.onEnded();
  }

  private async offerTo(peerID: string): Promise<void> {
    const pc = await this.ensurePeer(peerID);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this.send(peerID, { kind: "offer", sdp: offer.sdp, video: this.video });
  }

  private async ensurePeer(peerID: string): Promise<RTCPeerConnection> {
    const existing = this.peers.get(peerID);
    if (existing) return existing;
    const pc = new RTCPeerConnection({ iceServers: await this.getIceServers() });
    if (this.local) for (const t of this.local.getTracks()) pc.addTrack(t, this.local);
    pc.ontrack = (e) => this.events.onPeerStream(peerID, e.streams[0]);
    pc.onicecandidate = (e) => {
      if (e.candidate) this.send(peerID, { kind: "candidates", candidates: [e.candidate.toJSON()] });
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed" || pc.connectionState === "closed") this.removePeer(peerID);
    };
    this.setupWbChannel(peerID, pc);
    this.peers.set(peerID, pc);
    this.emitParticipants();
    return pc;
  }

  // setupWbChannel opens the per-peer NEGOTIATED whiteboard data channel (same fixed
  // id as the 1:1 path). Each pair carries its own channel; local ops fan out to all
  // open channels and inbound ops are author-stamped to THAT peer + de-duped by op id.
  private setupWbChannel(peerID: string, pc: RTCPeerConnection): void {
    const dc = pc.createDataChannel(WHITEBOARD_DC_LABEL, { negotiated: true, id: WHITEBOARD_DC_ID });
    dc.onmessage = (e) => {
      if (typeof e.data !== "string") return;
      const op = decode(e.data);
      if (!op || !this.wbDedup.first(op.id)) return; // unknown/malformed or already seen
      this.events.onWhiteboardOp?.(stampAuthor(op, peerID));
    };
    this.wbChannels.set(peerID, dc);
  }

  // sendOp fans a local whiteboard op out to every open peer channel.
  sendOp(op: WireOp): void {
    const data = encode(op);
    for (const dc of this.wbChannels.values()) {
      if (dc.readyState === "open") dc.send(data);
    }
  }

  private removePeer(peerID: string): void {
    const pc = this.peers.get(peerID);
    if (!pc) return;
    this.closeWbChannel(peerID);
    pc.close();
    this.peers.delete(peerID);
    this.pending.delete(peerID);
    this.events.onPeerStream(peerID, null);
    this.emitParticipants();
  }

  private closeWbChannel(peerID: string): void {
    const dc = this.wbChannels.get(peerID);
    if (!dc) return;
    dc.onmessage = null;
    try {
      dc.close();
    } catch {
      /* closing with the pc */
    }
    this.wbChannels.delete(peerID);
  }

  private async flush(peerID: string, pc: RTCPeerConnection): Promise<void> {
    const cands = this.pending.get(peerID);
    if (!cands) return;
    for (const c of cands) await pc.addIceCandidate(c).catch(() => undefined);
    this.pending.delete(peerID);
  }

  private queue(peerID: string, cand: RTCIceCandidateInit): void {
    const arr = this.pending.get(peerID) ?? [];
    arr.push(cand);
    this.pending.set(peerID, arr);
  }

  private emitParticipants(): void {
    this.events.onParticipants([...this.peers.keys()]);
  }

  private send(target: string, partial: Omit<GroupSignal, "call_id" | "target">): void {
    this.sendSignal(this.roomID, { call_id: this.roomID, target, ...partial });
  }
  private broadcast(partial: Omit<GroupSignal, "call_id" | "target">): void {
    this.sendSignal(this.roomID, { call_id: this.roomID, ...partial });
  }
}
